import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { withRouter } from 'react-router';

import { CenteredContainer, Title } from '@nerdwallet/currency';

import Hero from '../../employee-branding/hero/hero';
import ZigZag from '../../employee-branding/zig-zag/zig-zag';
import CompanyFooter from '../../employee-branding/company-footer/company-footer';

import grnhseTrackingCheck from '~/client/lib/company-content-src/grnhse-tracking-check';
import { CAREERS_TEAMS_PATH } from '~/client/lib/employee-branding/careers-breadcrumbs-paths';

const BENEFITS_COPY = [
  {
    header: 'Health and wellness',
    copy: (
      <Fragment>
        Competitive health insurance for you and your dependents, plus mental
        health support through Ginger. We offer everyone a wellness stipend for
        personal wellness and a WFH stipend to deck out our remote Nerds&apos;
        home offices.{' '}
      </Fragment>
    ),
  },
  {
    header: 'Retirement support',
    copy: (
      <Fragment>
        Along with competitive salaries, we offer retirement solutions, matching
        401Ks for all Nerds. We also offer legal support through a partnership
        with Rocket Lawyer.
      </Fragment>
    ),
  },
  {
    header: 'True work-life balance',
    copy: (
      <Fragment>
        We offer flexible time off as well as “Mental Health Days” throughout
        the year, so our Nerds can take time for themselves and come back
        refreshed. We also offer new parents generous leave time to bond with
        their new family members.
      </Fragment>
    ),
  },
];

const CAREER_BENEFITS = [
  {
    header: 'Career development',
    copy: (
      <Fragment>
        We offer company-wide education days as well as Nerdy “Expert” panels
        where you learn from your colleagues. We also offer every Nerd a chance
        to learn and grow as a team and as individuals through classes and
        coaching.
      </Fragment>
    ),
  },
  {
    header: 'Employee resource groups',
    copy: (
      <Fragment>
        We’ve got groups to help our Nerds feel together, even in a remote-first
        environment. These employee-led groups include groups for women,
        diversified allies, and nerd parents helping foster a diverse, inclusive
        workplace while staying connected.
      </Fragment>
    ),
  },
  {
    header: 'Employee engagement',
    copy: (
      <Fragment>
        We recognize our Nerds and celebrate whenever we can with annual
        traditions like NerdLove (a celebration of our employees), Nerd
        Anniversary celebrations, Little Nerds Day (bring your son or daughter
        to work events), charity auctions, and more.
        <br />
        <br />
        We also love to give back and provide donation matching for approved
        non-profits you choose to support.
      </Fragment>
    ),
  },
];

const BenefitsPage = ({ location: routerLocation }) => (
  <div>
    <div
      className={classNames([
        classes('margin-bottom-4'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <Hero
        backgroundColor="#DDFDF2"
        title="NerdWallet's benefits"
        copy="Our benefits are designed to keep our Nerds happy and healthy in their professional and personal lives."
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-Benefits-Mobile.jpg"
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-Benefits-Desktop.jpg"
      />
    </div>
    <CenteredContainer>
      <Title
        size="small"
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3'),
          classes('margin-bottom-5--wide'),
          classes('text-align-center'),
        ])}
        component="h2"
      >
        Keeping our Nerds happy and healthy
      </Title>
    </CenteredContainer>
    <ZigZag
      backgroundColor="#ffffff"
      color="neutral-darkest"
      copyArray={BENEFITS_COPY}
      imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_Benefits-01.jpg"
      imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_Benefits-01.jpg"
      imageAlt=""
      title=""
      imageFirst
      copyLeftMobile
    />
    <CenteredContainer>
      <Title
        size="small"
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-vertical-4'),
          classes('margin-vertical-5--wide'),
          classes('text-align-center'),
        ])}
        component="h2"
      >
        Helping our Nerds grow and stay connected
      </Title>
    </CenteredContainer>
    <div className={classNames([classes('margin-bottom-5')])}>
      <ZigZag
        backgroundColor="#ffffff"
        color="neutral-darkest"
        copyArray={CAREER_BENEFITS}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_Benefits-02.jpg"
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/zigzag/Company-Pages-Photography-Update-Collage_R1_v2_BK_Benefits-02.jpg"
        imageAlt=""
        title=""
        copyLeftMobile
      />
    </div>
    <CompanyFooter
      backgroundColor="#EEF7FF"
      contrastColor="#9DCDFA"
      title="Come nerd out with us"
      ctaText="see open positions"
      ctaUrl={grnhseTrackingCheck(CAREERS_TEAMS_PATH, routerLocation.search)}
      imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/footer/mobile/Careers-CTA-Illustration-Mobile_375.png"
      imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/footer/desktop/Careers-CTA-Illustration-Desktop.png"
      isSmall={false}
    />
  </div>
);

BenefitsPage.displayName = 'Benefits';

BenefitsPage.propTypes = {
  location: PropTypes.object,
};

const BenefitsPageWithRouter = withRouter(BenefitsPage);

export default BenefitsPageWithRouter;
